import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "../providers/UserProvider";
import { User } from "../storage/store";
import { isAdmin } from "../pages/Admin/paths";
import { AutomationService } from "./service";

// Whether the selected home has the Automation feature (the API answers its
// capabilities), the names and types of its devices for the words on the
// cards, and what the home and its devices support, as the app's
// app/automation/AutomationProvider.js. The old routines, scenes and rules are
// retired (hashh-api-v2 f0f593c), so every home has it.

// Types the old routines, scenes and rules moved into: always shown, as their
// `ready` only says whether every old item of the type moved.
const RETIRED_SYSTEM_TYPES = ["tap", "schedule", "sun", "rule"];

// Whether the home offers a type of automation (capabilities.types[].ready).
export function typeReady(capabilities, type) {
  const entry = (capabilities?.types || []).find((item) => item.type === type);
  if (!entry) return false;
  if (RETIRED_SYSTEM_TYPES.includes(type)) return true;
  if (typeof entry.ready === "boolean") return entry.ready;
  // an API from before the migration step doesn't send `ready`; only test homes are switched on then
  return type === "nfc" || entry.mode !== "legacy";
}

const AutomationContext = createContext({
  homeId: null,
  on: false,
  checked: false,
  capabilities: null,
  ready: () => false,
  device: () => null,
  homeDevice: () => null,
  lookup: { name: () => "A device", device: () => null },
  refresh: async () => null,
});

export const useAutomationFeature = () => useContext(AutomationContext);

export const AutomationProvider = ({ children }) => {
  const { PropertyId_c, allDevices } = useUser();
  const homeId = isAdmin() ? null : PropertyId_c ?? User.getPropertyId();
  // homeId → capabilities (null while the feature is off); kept when a later check fails
  const known = useRef({});
  const [answer, setAnswer] = useState({ homeId: null, capabilities: null, checked: false });

  const refresh = useCallback(async () => {
    if (!homeId) return null;
    try {
      known.current[homeId] = await AutomationService.capabilities(homeId);
    } catch (error) {
      // offline or a server error: keep what this home answered last
    }
    const capabilities = known.current[homeId] ?? null;
    setAnswer({ homeId, capabilities, checked: true });
    return capabilities;
  }, [homeId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // A home can be switched on at any time: check again when the tab comes back.
  useEffect(() => {
    const onVisible = () => document.visibilityState === "visible" && refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  const value = useMemo(() => {
    const current = answer.homeId === homeId;
    const capabilities = current ? answer.capabilities : known.current[homeId] ?? null;
    const devices = new Map((capabilities?.devices || []).map((device) => [device.applianceId, device]));
    const homeDevices = new Map((allDevices || []).map((device) => [device.applianceId, device]));
    return {
      homeId,
      on: Boolean(capabilities),
      checked: current && answer.checked,
      capabilities,
      ready: (type) => Boolean(capabilities) && typeReady(capabilities, type),
      // what a device supports in automations (capabilities.devices)
      device: (applianceId) => devices.get(applianceId) || null,
      // the home's full device record (pin, traits, model), which actions are built from
      homeDevice: (applianceId) => homeDevices.get(applianceId) || null,
      // names and types for the words on cards; the home's own list fills in
      // for devices not in the capabilities
      lookup: {
        name: (applianceId) => devices.get(applianceId)?.name || homeDevices.get(applianceId)?.name || "A device",
        device: (applianceId) => devices.get(applianceId) || homeDevices.get(applianceId) || null,
      },
      refresh,
    };
  }, [answer, homeId, allDevices, refresh]);

  return <AutomationContext.Provider value={value}>{children}</AutomationContext.Provider>;
};

export default AutomationProvider;
